import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { ClinicalInsightData } from '../../../../../types/live-insights';
import { useTranslation } from 'react-i18next';

interface Props {
    data: ClinicalInsightData;
}

export const LiveAlertCard: React.FC<Props> = ({ data }) => {
    const { t } = useTranslation();
    const criticality = data.metadata?.priority || 'medium';

    const getAlertStyle = (crit: string) => {
        const lower = crit.toLowerCase();
        if (lower.includes("low") || lower.includes("baja")) {
            return {
                border: "border-accent/20",
                bg: "bg-accent-tint",
                text: "text-accent-on-tint",
                badge: "bg-accent-tint text-accent-on-tint text-accent-text",
                icon: "text-accent-text"
            };
        }
        if (lower.includes("medium") || lower.includes("media") || lower.includes("yellow")) {
            return {
                border: "border-yellow-100 dark:border-yellow-900/30",
                bg: "bg-yellow-50/50 dark:bg-yellow-900/10",
                text: "text-yellow-800 dark:text-yellow-200",
                badge: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
                icon: "text-yellow-500"
            };
        }
        return {
            border: "border-red-100 dark:border-red-900/30",
            bg: "bg-red-50/50 dark:bg-red-900/10",
            text: "text-red-800 dark:text-red-200",
            badge: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
            icon: "text-red-500"
        };
    };

    const style = getAlertStyle(criticality);
    const normalized = criticality.toLowerCase().includes("low") || criticality.toLowerCase().includes("baja") ? "low" :
        criticality.toLowerCase().includes("medium") || criticality.toLowerCase().includes("media") ? "medium" : "critical";

    return (
        <div className={`group relative overflow-hidden p-4 rounded-xl shadow-sm border transition-all hover:shadow-md ${style.border} ${style.bg} backdrop-blur-md`}>
            {/* Visual indicator for Zen UX */}
            <div className={`absolute left-0 top-0 h-full w-1 opacity-60 ${normalized === 'critical' ? 'bg-red-500' : normalized === 'medium' ? 'bg-yellow-500' : 'bg-accent'}`} />

            <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                    <div className={`p-1 rounded-full ${normalized === 'critical' ? 'bg-red-100' : normalized === 'medium' ? 'bg-yellow-100' : 'bg-accent-tint'}`}>
                        <AlertTriangle className={`w-3.5 h-3.5 ${style.icon}`} />
                    </div>
                    <span className={`text-sm font-bold tracking-tight ${style.text}`}>{data.metadata?.alert_name || data.title}</span>
                </div>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${style.badge}`}>
                    {t(`clinicalAlerts.modal.criticality.${normalized}`)}
                </span>
            </div>
            <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed italic opacity-90">
                "{data.content.replace('Detección en tiempo real: ', '').replace(/'/g, '')}"
            </p>
        </div>
    );
};
